import type { ParsedUrlQuery } from 'querystring';

import { useMemo } from 'react';
import type { ContestType } from '@betterpool/api-types/contests-service';
import useTranslation from 'next-translate/useTranslation';

import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import { FEATURE } from '~/common/enums/feature.enum';
import { useTreatment } from '~/domains/common/hooks/useTreatment';
import { PayoutType } from '~/domains/commissioner/interfaces/contest.interface';
import { filterDescriptorFactory } from '~/domains/common/components/Filters/filterDescriptorFactory';
import type { TFilterObject } from '~/domains/common/components/Filters/filterDescriptorFactory';
import { EntryType } from '~/services/contests/enums/entry-type.enum';
import { SortDirection, SortOptions } from '~/services/contests/enums/contest-sort-options.enum';


import { ViewStyle } from '../../Lobby.enums';

const useFieldDescriptors = () => {
  const { t } = useTranslation('common');
  const { treatment } = useTreatment(FEATURE.CONFIG_LOBBY_FILTERS);

  const getGameTypeOptions = useMemo(() => {
    if (treatment?.treatment === 'on' && treatment?.config) {
      return (treatment.config as (ContestType & 'all')[]).map((type) => ({
        value: type,
        label: t(`filters.gameType.options.${type}`),
      }));
    }

    return [];
  }, [t, treatment]);

  const FilterDescriptors: Record<string, TFilterObject> = useMemo(
    () => ({
      SORT_BY: {
        object: {
          title: t('filters.sortBy.title'),
          key: 'SORT_BY',
          type: 'single-select',
          options: [
            // EDGE-30638: temporarily hidden for the $21M push (default is now "Highest total
            // prizes"). Revert after Sept 13 (EDGE-30642): restore the line below.
            // { label: t('filters.sortBy.options.default') },
            {
              value: { option: SortOptions.CONTEST_NAME, dir: SortDirection.ASC },
              label: t('filters.sortBy.options.contestName'),
            },
            {
              value: { option: SortOptions.START_DATE, dir: SortDirection.ASC },
              label: t('filters.sortBy.options.nextSlateStarting'),
            },
            {
              value: { option: SortOptions.ENTRIES_REMAINING, dir: SortDirection.DESC },
              label: t('filters.sortBy.options.entriesRemaining'),
            },
            {
              value: { option: SortOptions.TOTAL_PRIZES, dir: SortDirection.DESC },
              label: t('filters.sortBy.options.highestTotalPrizes'),
            },
            {
              value: { option: SortOptions.ENTRY_FEE, dir: SortDirection.DESC },
              label: t('filters.sortBy.options.highestEntryFee'),
            },
          ],
        },
        fromQuery: (query: ParsedUrlQuery) => {
          const sortBy = query[URLQueryKeys.SORT_BY];
          if (!sortBy) {
            // EDGE-30638: temporary $21M-push default — preselect "Highest total prizes".
            // Revert after Sept 13 (EDGE-30642): restore `return null;`
            return [{ option: SortOptions.TOTAL_PRIZES, dir: SortDirection.DESC }];
          }

          return [
            {
              option: query[URLQueryKeys.SORT_BY],
              dir: query[URLQueryKeys.SORT_ORDER] ?? SortDirection.ASC,
            },
          ];
        },
        toQuery: (value: { option: string; dir: string }[]) => {
          const v = value?.[0];

          if (!v) {
            return {
              [URLQueryKeys.SORT_BY]: null,
              [URLQueryKeys.SORT_ORDER]: null,
            };
          }

          return {
            [URLQueryKeys.SORT_BY]: v.option,
            [URLQueryKeys.SORT_ORDER]: v.dir,
          };
        },
      },
      GAME_TYPE: {
        object: {
          title: t('filters.gameType.title'),
          key: URLQueryKeys.CONTEST_TYPE,
          type: 'multi-select',
          options: [
            { value: undefined, label: t('filters.gameType.options.all') },
            ...getGameTypeOptions,
          ],
        },
        fromQuery: (query: ParsedUrlQuery) => {
          const urlQuery = query[URLQueryKeys.CONTEST_TYPE] as string;
          return !urlQuery ? undefined : urlQuery.split(',');
        },
        toQuery: (value: string[]) => {
          if (!value || value.some((v) => v === undefined)) {
            return { [URLQueryKeys.CONTEST_TYPE]: null };
          }
          return { [URLQueryKeys.CONTEST_TYPE]: value.filter(Boolean).join(',') };
        },
      },
      PAYOUT_FORMATS: {
        object: {
          title: t('filters.payoutFormats.title'),
          key: URLQueryKeys.PAYOUT_FORMAT,
          type: 'single-select',
          options: [
            {
              value: undefined,
              label: t('filters.payoutFormats.options.all'),
            },
            {
              value: PayoutType.TOP_X,
              label: t('filters.payoutFormats.options.topX'),
            },
            {
              value: PayoutType.MULTIPLIER,
              label: t('filters.payoutFormats.options.multiplier'),
            },
          ],
        },
      },
      ENTRY_TYPES: {
        object: {
          title: t('filters.entryTypes.title'),
          key: URLQueryKeys.ENTRY_TYPES,
          type: 'single-select',
          options: [
            {
              value: undefined,
              label: t('filters.entryTypes.options.all'),
            },
            {
              value: EntryType.MULTI_ENTRY,
              label: t('filters.entryTypes.options.multiEntry'),
            },
            {
              value: EntryType.SINGLE_ENTRY,
              label: t('filters.entryTypes.options.singleEntry'),
            },
          ],
        },
      },
      ENTRY_FEE: {
        object: {
          title: t('filters.entryFee.title'),
          key: URLQueryKeys.ENTRY_FEE,
          type: 'single-select',
          options: [
            {
              value: undefined,
              label: t('filters.entryFee.options.all'),
            },
            {
              value: '0,0',
              label: t('filters.entryFee.options.noEntryFee'),
            },
            {
              value: '0,10',
              label: t('filters.entryFee.options.tier1'),
            },
            {
              value: '11,100',
              label: t('filters.entryFee.options.tier2'),
            },
            {
              value: '101,1000',
              label: t('filters.entryFee.options.tier3'),
            },
            {
              value: '1001,10000',
              label: t('filters.entryFee.options.tier4'),
            },
          ],
        },
      },
      HIDE_UNLISTED: {
        object: {
          title: t('filters.hideUnlisted.title'),
          key: URLQueryKeys.HIDE_UNLISTED,
          type: 'single-select',
          options: [
            {
              value: undefined,
              label: t('filters.hideUnlisted.options.all'),
            },
            {
              value: 'true',
              label: t('filters.hideUnlisted.options.show'),
            },
          ],
        },
      },
      VIEW_AS: {
        object: {
          title: t('filters.viewStyle.title'),
          key: URLQueryKeys.VIEW,
          type: 'single-select',
          options: [
            {
              value: undefined,
              label: t('filters.viewStyle.options.table'),
            },
            {
              value: ViewStyle.GRID,
              label: t('filters.viewStyle.options.grid'),
            },
          ],
        },
      },
    }),
    [t, getGameTypeOptions]
  );

  return filterDescriptorFactory(FilterDescriptors);
};

export default useFieldDescriptors;
